import { Button } from "@/ui";
import { Menu, X, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";

function cx(...classes: Array<string | undefined | false | null>) {
  return classes.filter(Boolean).join(" ");
}

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<null | "solutions" | "resources" | "about">(null);
  const desktopNavRef = useRef<HTMLElement | null>(null);
  const [location] = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Match ucaremr-website behavior: switch styling based on page scroll position
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (!openDropdown) return;
      const nav = desktopNavRef.current;
      if (!nav) return;
      if (!nav.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openDropdown]);

  const logoImage = isScrolled
    ? "/assets/logo/aiscendus-blue-bg.png"
    : "/assets/logo/aiscendus-white-bg.png";

  const ribbonBg = isScrolled ? "#1356a6" : "#ffffff";

  const isActivePage = (path: string) => {
    if (path === "/" && location === "/") return true;
    if (path !== "/" && location.startsWith(path)) return true;
    return false;
  };

  const getActiveLinkClass = (path: string) => {
    const baseClass = "transition-colors";
    const activeClass = isScrolled ? "text-white font-semibold" : "text-[#1356a6] font-semibold";
    const inactiveClass = isScrolled ? "text-white/90 hover:text-white" : "text-gray-700 hover:text-[#1356a6]";
    
    return `${baseClass} ${isActivePage(path) ? activeClass : inactiveClass}`;
  };


  const getSolutionsDropdownClass = () => {
    const solutionsPaths = ["/solutions/clinical", "/solutions/occupational-medicine", "/solutions/practice-management", "/solutions/revenue-cycle-management"];
    const isSolutionsActive = solutionsPaths.some(path => isActivePage(path));
    const baseClass = "flex items-center gap-1 transition-colors";
    const activeClass = isScrolled ? "text-white font-semibold" : "text-[#1356a6] font-semibold";
    const inactiveClass = isScrolled ? "text-white/90 hover:text-white" : "text-gray-700 hover:text-[#1356a6]";
    
    return `${baseClass} ${isSolutionsActive ? activeClass : inactiveClass}`;
  };

  const getResourcesDropdownClass = () => {
    const resourcesPaths = ["/news", "/careers", "/blogs"];
    const isResourcesActive = resourcesPaths.some(path => isActivePage(path));
    const baseClass = "flex items-center gap-1 transition-colors";
    const activeClass = isScrolled ? "text-white font-semibold" : "text-[#1356a6] font-semibold";
    const inactiveClass = isScrolled ? "text-white/90 hover:text-white" : "text-gray-700 hover:text-[#1356a6]";
    
    return `${baseClass} ${isResourcesActive ? activeClass : inactiveClass}`;
  };

  const getAboutDropdownClass = () => {
    const aboutPaths = ["/contact"];
    const isAboutActive = aboutPaths.some(path => isActivePage(path));
    const baseClass = "flex items-center gap-1 transition-colors";
    const activeClass = isScrolled ? "text-white font-semibold" : "text-[#1356a6] font-semibold";
    const inactiveClass = isScrolled ? "text-white/90 hover:text-white" : "text-gray-700 hover:text-[#1356a6]";
    
    return `${baseClass} ${isAboutActive ? activeClass : inactiveClass}`;
  };

  const getMobileActiveLinkClass = (path: string) => {
    const baseClass = "block transition-colors";
    const activeClass = isScrolled ? "text-white font-semibold" : "text-[#1356a6] font-semibold";
    const inactiveClass = isScrolled ? "text-white/90 hover:text-white" : "text-gray-700 hover:text-[#1356a6]";
    
    return `${baseClass} ${isActivePage(path) ? activeClass : inactiveClass}`;
  };

  return (
    <header
      className={cx(
        "fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300",
        isScrolled ? "shadow-md" : "border-b border-gray-200",
      )}
      style={{ backgroundColor: ribbonBg }}
    >
      <div className="w-full pl-3 pr-6 lg:pl-4 lg:pr-12 flex h-16 items-center">
        <Link href="/" className="flex items-center space-x-3 hover-elevate" data-testid="link-home">
          <img 
            src={logoImage} 
            alt="AIscendus Logo" 
            className="h-12 w-auto rounded-lg"
            data-testid="img-logo"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav ref={desktopNavRef as any} className="hidden md:flex items-center space-x-8 ml-auto">
          {/* Home - direct link */}
          <Link href="/" className={getActiveLinkClass("/")} data-testid="link-home-nav">
            Home
          </Link>

          {/* Solutions dropdown (click-to-open) */}
          <div className="relative">
            <button
              type="button"
              className={getSolutionsDropdownClass()}
              data-testid="dropdown-solutions"
              aria-haspopup="menu"
              aria-expanded={openDropdown === "solutions"}
              onClick={() => setOpenDropdown((v) => (v === "solutions" ? null : "solutions"))}
            >
              Solutions
              <ChevronDown className="h-4 w-4" />
            </button>
            <div
              className={cx(
                "absolute left-0 mt-2 min-w-60 rounded-md border border-border bg-background shadow-md z-50",
                openDropdown === "solutions" ? "block" : "hidden",
              )}
              role="menu"
            >
              <Link
                href="/solutions/clinical"
                className="block px-3 py-2 text-sm hover:bg-muted"
                data-testid="link-solutions-clinical"
                onClick={() => setOpenDropdown(null)}
              >
                Clinical
              </Link>
              <Link
                href="/solutions/occupational-medicine"
                className="block px-3 py-2 text-sm hover:bg-muted"
                data-testid="link-solutions-occupational-medicine"
                onClick={() => setOpenDropdown(null)}
              >
                Occupational Medicine
              </Link>
              <Link
                href="/solutions/practice-management"
                className="block px-3 py-2 text-sm hover:bg-muted"
                data-testid="link-solutions-practice-management"
                onClick={() => setOpenDropdown(null)}
              >
                Practice Management
              </Link>
              <Link
                href="/solutions/revenue-cycle-management"
                className="block px-3 py-2 text-sm hover:bg-muted"
                data-testid="link-solutions-revenue-cycle-management"
                onClick={() => setOpenDropdown(null)}
              >
                Revenue Cycle Management
              </Link>
            </div>
          </div>

          {/* Platform - direct link */}
          <Link href="/platform" className={getActiveLinkClass("/platform")} data-testid="link-platform">
            Platform
          </Link>

          <div className="relative">
            <button
              type="button"
              className={getResourcesDropdownClass()}
              data-testid="dropdown-resources"
              aria-haspopup="menu"
              aria-expanded={openDropdown === "resources"}
              onClick={() => setOpenDropdown((v) => (v === "resources" ? null : "resources"))}
            >
              Resources
              <ChevronDown className="h-4 w-4" />
            </button>
            <div
              className={cx(
                "absolute left-0 mt-2 min-w-56 rounded-md border border-border bg-background shadow-md z-50",
                openDropdown === "resources" ? "block" : "hidden",
              )}
              role="menu"
            >
              <Link
                href="/news"
                className="block px-3 py-2 text-sm hover:bg-muted"
                data-testid="link-news"
                onClick={() => setOpenDropdown(null)}
              >
                News
              </Link>
              <Link
                href="/careers"
                className="block px-3 py-2 text-sm hover:bg-muted"
                data-testid="link-careers"
                onClick={() => setOpenDropdown(null)}
              >
                Careers
              </Link>
              <Link
                href="/blogs"
                className="block px-3 py-2 text-sm hover:bg-muted"
                data-testid="link-blogs"
                onClick={() => setOpenDropdown(null)}
              >
                Blogs
              </Link>
            </div>
          </div>
          <div className="relative">
            <button
              type="button"
              className={getAboutDropdownClass()}
              data-testid="dropdown-about"
              aria-haspopup="menu"
              aria-expanded={openDropdown === "about"}
              onClick={() => setOpenDropdown((v) => (v === "about" ? null : "about"))}
            >
              About
              <ChevronDown className="h-4 w-4" />
            </button>
            <div
              className={cx(
                "absolute left-0 mt-2 min-w-56 rounded-md border border-border bg-background shadow-md z-50",
                openDropdown === "about" ? "block" : "hidden",
              )}
              role="menu"
            >
              <Link
                href="/contact"
                className="block px-3 py-2 text-sm hover:bg-muted"
                data-testid="link-contact"
                onClick={() => setOpenDropdown(null)}
              >
                Contact
              </Link>
            </div>
          </div>
        </nav>


        {/* Mobile Menu Button */}
        <Button 
          variant="ghost" 
          size="icon" 
          className={cx(
            "md:hidden ml-auto",
            isScrolled ? "text-white hover:bg-white/10" : "text-gray-700 hover:bg-gray-100",
          )}
          onClick={toggleMenu}
          data-testid="button-menu-toggle"
        >
          {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div
          className={cx(
            "md:hidden border-t",
            isScrolled ? "border-white/20" : "border-gray-200",
          )}
          style={{ backgroundColor: ribbonBg }}
        >
          <nav className="w-full pl-3 pr-6 py-4 space-y-4">
            <Link href="/" className={getMobileActiveLinkClass("/")} data-testid="link-mobile-home" onClick={() => setIsMenuOpen(false)}>
              Home
            </Link>
            <div className="space-y-2">
              <div className={`text-sm font-medium mb-2 ${
                ["/solutions/clinical", "/solutions/occupational-medicine", "/solutions/practice-management", "/solutions/revenue-cycle-management"].some(path => isActivePage(path)) 
                  ? (isScrolled ? "text-white font-semibold" : "text-[#1356a6] font-semibold")
                  : (isScrolled ? "text-white/90" : "text-gray-700")
              }`}>Solutions</div>
              <div className="pl-4 space-y-1">
                <Link href="/solutions/clinical" className={getMobileActiveLinkClass("/solutions/clinical")} data-testid="link-mobile-solutions-clinical" onClick={() => setIsMenuOpen(false)}>
                  Clinical
                </Link>
                <Link href="/solutions/occupational-medicine" className={getMobileActiveLinkClass("/solutions/occupational-medicine")} data-testid="link-mobile-solutions-occupational-medicine" onClick={() => setIsMenuOpen(false)}>
                  Occupational Medicine
                </Link>
                <Link href="/solutions/practice-management" className={getMobileActiveLinkClass("/solutions/practice-management")} data-testid="link-mobile-solutions-practice-management" onClick={() => setIsMenuOpen(false)}>
                  Practice Management
                </Link>
                <Link href="/solutions/revenue-cycle-management" className={getMobileActiveLinkClass("/solutions/revenue-cycle-management")} data-testid="link-mobile-solutions-revenue-cycle-management" onClick={() => setIsMenuOpen(false)}>
                  Revenue Cycle Management
                </Link>
              </div>
            </div>
            <Link href="/platform" className={getMobileActiveLinkClass("/platform")} data-testid="link-mobile-platform" onClick={() => setIsMenuOpen(false)}>
              Platform
            </Link>
            <div>
              <div className={`text-sm font-medium mb-2 ${
                ["/news", "/careers", "/blogs"].some(path => isActivePage(path)) 
                  ? (isScrolled ? "text-white font-semibold" : "text-[#1356a6] font-semibold")
                  : (isScrolled ? "text-white/90" : "text-gray-700")
              }`}>Resources</div>
              <div className="space-y-2 pl-4">
                <Link href="/news" className={`block text-sm transition-colors ${isActivePage("/news") ? (isScrolled ? "text-white font-semibold" : "text-[#1356a6] font-semibold") : (isScrolled ? "text-white/90 hover:text-white" : "text-gray-700 hover:text-[#1356a6]")}`} data-testid="link-mobile-news" onClick={() => setIsMenuOpen(false)}>
                  News
                </Link>
                <Link href="/careers" className={`block text-sm transition-colors ${isActivePage("/careers") ? (isScrolled ? "text-white font-semibold" : "text-[#1356a6] font-semibold") : (isScrolled ? "text-white/90 hover:text-white" : "text-gray-700 hover:text-[#1356a6]")}`} data-testid="link-mobile-careers" onClick={() => setIsMenuOpen(false)}>
                  Careers
                </Link>
                <Link href="/blogs" className={`block text-sm transition-colors ${isActivePage("/blogs") ? (isScrolled ? "text-white font-semibold" : "text-[#1356a6] font-semibold") : (isScrolled ? "text-white/90 hover:text-white" : "text-gray-700 hover:text-[#1356a6]")}`} data-testid="link-mobile-blogs" onClick={() => setIsMenuOpen(false)}>
                  Blogs
                </Link>
              </div>
            </div>
            <div>
              <div className={`text-sm font-medium mb-2 ${
                ["/contact"].some(path => isActivePage(path)) 
                  ? (isScrolled ? "text-white font-semibold" : "text-[#1356a6] font-semibold")
                  : (isScrolled ? "text-white/90" : "text-gray-700")
              }`}>About</div>
              <div className="space-y-2 pl-4">
                <Link href="/contact" className={`block text-sm transition-colors ${isActivePage("/contact") ? (isScrolled ? "text-white font-semibold" : "text-[#1356a6] font-semibold") : (isScrolled ? "text-white/90 hover:text-white" : "text-gray-700 hover:text-[#1356a6]")}`} data-testid="link-mobile-contact" onClick={() => setIsMenuOpen(false)}>
                  Contact
                </Link>
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

